import React, { useEffect, useMemo, useRef, useState } from "react";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import pluralize from "pluralize";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  RiAddCircleLine,
  RiCheckboxCircleLine,
  RiCloseLine,
  RiErrorWarningLine,
  RiSearchLine,
  RiIndeterminateCircleLine,
} from "@remixicon/react";
import { useTheme } from "@mui/material/styles";

import {
  DeliveriesAPIResponseT,
  AutoSizerPropsT,
  DeliveryItemT,
  DeliveryTypeT,
  deliveryTypeLabels,
  DialogActionsT,
} from "../../../types/ScheduleForm";
import useGaneshApi from "../../../hooks/useGaneshApi";
import { filterData, groupDataByType } from "../../../utils/scheduleFormUtils";
import DeliveriesComboItemIcon from "./DeliveriesComboItemIcon";
import { formatDateHumanReadable } from "utils/formatting";

const deliveryTypeMap = {
  delivery: "/v1/deliveries/active",
  fulfillmentGroup: "/v1/deliveries/active/by_fg",
  customer: "/v1/deliveries/active/by_customer",
};

function isGroupedItem(
  item: DeliveryItemT | { name: string; items: DeliveryItemT[] }
): item is { name: string; items: DeliveryItemT[] } {
  return "items" in item;
}

const GROUP_HEADER_HEIGHT = 36;
const LIST_ITEM_HEIGHT = 56;

interface DeliveriesComboProps {
  selectedItems: DeliveryItemT[];
  onSelectionChange: (selectedItems: DeliveryItemT[]) => void;
  action?: DialogActionsT;
}

const DeliveriesCombo: React.FC<DeliveriesComboProps> = ({
  selectedItems,
  onSelectionChange,
  action = "ADD",
}) => {
  const theme = useTheme();
  const { apiRequest } = useGaneshApi();

  const isDisabled =
    action === "ADD_ONE" || action === "CHANGE" || action === "REMOVE";
  const [deliveryType, setDeliveryType] = useState<DeliveryTypeT>("delivery");
  const [searchTerm, setSearchTerm] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(isDisabled);

  const listRef = useRef<List>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useQuery<DeliveriesAPIResponseT, Error>({
    queryKey: ["combo", deliveryType],
    queryFn: async () => {
      const response = await apiRequest(
        deliveryTypeMap[deliveryType as keyof typeof deliveryTypeMap],
        {
          method: "GET",
        }
      );
      return response as DeliveriesAPIResponseT;
    },
    retry: 5,
  });

  // memoized values
  const groupedData = useMemo(() => {
    if (!data) return [];

    let filteredItems = filterData(data.data, searchTerm, deliveryType);

    if (showSelectedOnly) {
      filteredItems = filteredItems.filter((item) =>
        selectedItems.some((i) => i.delivery_id === item.delivery_id)
      );
    }

    return groupDataByType(filteredItems, deliveryType);
  }, [data, searchTerm, deliveryType, showSelectedOnly, selectedItems]);

  // handlers
  const handleDeliveryTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDeliveryType(event.target.value as DeliveryTypeT);
    setShowSelectedOnly(false);
    setSearchTerm("");
    onSelectionChange([]);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const toggleSelection = (
    item: DeliveryItemT | { name: string; items: DeliveryItemT[] }
  ) => {
    if (!isGroupedItem(item)) {
      const newSelectedItems = selectedItems.some(
        (i) => i.delivery_id === item.delivery_id
      )
        ? selectedItems.filter((i) => i.delivery_id !== item.delivery_id)
        : [...selectedItems, item];

      if (showSelectedOnly && newSelectedItems.length === 0) {
        setShowSelectedOnly(false);
      }

      onSelectionChange(newSelectedItems);
    }
  };

  const toggleGroupSelection = (childItems: DeliveryItemT[]) => {
    const groupItemIds = new Set(childItems.map((item) => item.delivery_id));
    const otherItems = selectedItems.filter(
      (item) => !groupItemIds.has(item.delivery_id)
    );

    const allSelected = childItems.every((item) =>
      selectedItems.some(
        (selectedItem) => selectedItem.delivery_id === item.delivery_id
      )
    );

    const newSelectedItems = allSelected
      ? otherItems
      : [...otherItems, ...childItems];
    onSelectionChange(newSelectedItems);
  };

  // force reset group data changes to keep the virtualized list squared away
  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [groupedData]);

  useEffect(() => {
    if (selectedItems.length === 0) {
      setShowSelectedOnly(false);
    }
  }, [selectedItems]);

  const renderGroup = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const group = groupedData[index];
    const isGrouped = isGroupedItem(group);
    const groupName = isGrouped ? group.name : "";
    const items = isGrouped ? group.items : [group];
    const groupSelectionState = useMemo(() => {
      if (!isGrouped) return "none";
      const selectedCount = items.filter((item) =>
        selectedItems.some((i) => i.delivery_id === item.delivery_id)
      ).length;
      if (selectedCount === 0) return "none";
      if (selectedCount === items.length) return "all";
      return "some";
    }, [isGrouped, items, selectedItems]);

    return (
      <div style={style}>
        {isGrouped && (
          <ListItem
            style={{
              backgroundColor: theme.palette.grey[50],
              padding: "0 16px",
              height: GROUP_HEADER_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "bold",
                textTransform: "uppercase",
                fontSize: "12px",
                color: theme.palette.text.secondary,
              }}
            >
              {groupName}
            </Typography>
            {!showSelectedOnly && (
              <IconButton
                edge="end"
                aria-label="toggle group selection"
                onClick={() => toggleGroupSelection(items)}
                size="small"
              >
                {groupSelectionState === "all" && (
                  <RiCheckboxCircleLine color={theme.palette.success.light} />
                )}
                {groupSelectionState === "some" && (
                  <RiIndeterminateCircleLine color={theme.palette.info.light} />
                )}
                {groupSelectionState === "none" && <RiAddCircleLine />}
              </IconButton>
            )}
          </ListItem>
        )}
        {items.map((item) => (
          <ListItem
            key={item.delivery_id}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "4px 16px",
              height: LIST_ITEM_HEIGHT,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
            disablePadding
          >
            <Box flexGrow={1}>
              <Typography variant="body2">{item.delivery_name}</Typography>
              <Typography variant="caption" color="text.secondary">
                ID: {item.delivery_id} | License End:{" "}
                {formatDateHumanReadable(item.license_end_date)}
                {item.group_name &&
                  ` | ${deliveryTypeLabels[deliveryType]}: ${item.group_name}`}
              </Typography>
            </Box>
            <IconButton
              edge="end"
              aria-label="Toggle selection"
              onClick={() => toggleSelection(item)}
              size="small"
              disabled={isDisabled}
            >
              <DeliveriesComboItemIcon
                isSelected={selectedItems.some(
                  (i) => i.delivery_id === item.delivery_id
                )}
                action={action}
              />
            </IconButton>
          </ListItem>
        ))}
      </div>
    );
  };

  const DEFAULT_BOX_HEIGHT = 240;

  const boxHeight =
    action !== "ADD" && selectedItems.length < 5
      ? selectedItems.length * LIST_ITEM_HEIGHT
      : DEFAULT_BOX_HEIGHT;

  return (
    <FormControl component="fieldset" sx={{ width: "100%" }}>
      <RadioGroup
        aria-label="type"
        name="type"
        value={deliveryType}
        onChange={handleDeliveryTypeChange}
      >
        <Box display="flex" flexDirection="row" gap={1} mb={1}>
          <FormControlLabel
            value="delivery"
            control={<Radio size="small" />}
            label="Delivery"
            disabled={isDisabled}
          />
          <FormControlLabel
            value="fulfillmentGroup"
            control={<Radio size="small" />}
            label="Fulfillment Group"
            disabled={isDisabled}
          />
          <FormControlLabel
            value="customer"
            control={<Radio size="small" />}
            label="Customer"
            disabled={isDisabled}
          />
        </Box>
      </RadioGroup>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder={`Search ${pluralize(
          deliveryTypeLabels[deliveryType],
          2
        )}...`}
        sx={{
          marginBottom: 0,
          "& .MuiOutlinedInput-root": {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            "& fieldset": {
              borderBottomWidth: 1,
            },
          },
        }}
        value={searchTerm}
        onChange={handleSearchChange}
        onKeyDown={handleSearchKeyDown}
        inputRef={searchInputRef}
        autoComplete="off"
        disabled={isDisabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <RiSearchLine size={16} />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton
                aria-label="clear search"
                onClick={handleClearSearch}
                edge="end"
                size="small"
              >
                <RiCloseLine />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Box
        mt={0}
        height={boxHeight}
        borderRadius="0 0 4px 4px"
        overflow={boxHeight === DEFAULT_BOX_HEIGHT ? "auto" : "hidden"}
        sx={{
          border: "1px solid #bdbdbd",
          borderTop: "none",
        }}
      >
        {isLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress
              size={20}
              sx={{ marginRight: 1, verticalAlign: "middle" }}
            />
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        )}
        {!isLoading && error && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={2}
          >
            <RiErrorWarningLine
              size={20}
              color={theme.palette.error.main}
              style={{ marginRight: "4px" }}
            />
            <Typography
              variant="body2"
              component="div"
              align="center"
              color="error"
            >
              Error loading data
            </Typography>
          </Box>
        )}
        {!isLoading && !error && groupedData && groupedData.length > 0 && (
          <AutoSizer>
            {({ height, width }: AutoSizerPropsT) => (
              <List
                ref={listRef}
                height={height}
                itemCount={groupedData.length}
                itemSize={(index) => {
                  const group = groupedData[index];
                  const itemCount = isGroupedItem(group)
                    ? group.items.length
                    : 1;
                  return (
                    (isGroupedItem(group) ? GROUP_HEADER_HEIGHT : 0) +
                    itemCount * LIST_ITEM_HEIGHT
                  );
                }}
                width={width}
                overscanCount={5}
              >
                {renderGroup}
              </List>
            )}
          </AutoSizer>
        )}
        {!isLoading && !error && (!groupedData || groupedData.length === 0) && (
          <Typography
            variant="body2"
            component="div"
            align="center"
            color="text.secondary"
            py={2}
          >
            No {pluralize(deliveryTypeLabels[deliveryType], 2)} found
          </Typography>
        )}
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={2}
        mb={1}
        px={1}
      >
        <FormControlLabel
          control={
            <Switch
              disabled={isDisabled}
              size="small"
              checked={showSelectedOnly}
              onChange={(e) => setShowSelectedOnly(e.target.checked)}
              name="showSelectedOnly"
            />
          }
          label="Show selected only"
        />
        <Typography variant="body2">
          {selectedItems.length} {pluralize("item", selectedItems.length)}{" "}
          selected
        </Typography>
      </Box>
    </FormControl>
  );
};

export default DeliveriesCombo;
